import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import BulkActionContext from '../context'
import PropTypes from 'prop-types'

const BulkActionProvider = memo(
  ({
    entities,
    children,
    preSelected,
    idSelector = entity => entity.id,
    selected,
    onChange
  }) => {
    const [_selected, _setSelected] = useState({})

    selected = selected || _selected
    const setSelected = onChange || _setSelected

    useEffect(() => {
      if (preSelected) {
        setSelected(preSelected)
      }
    }, [preSelected])

    const toggleItem = useCallback(
      id => {
        const selectedCopy = { ...selected }
        selectedCopy[id] ? delete selectedCopy[id] : (selectedCopy[id] = true)
        setSelected(selectedCopy)
      },
      [selected]
    )

    const selectAll = useCallback(() => {
      const newSelected = {}
      entities.forEach(entity => (newSelected[idSelector(entity)] = true))
      setSelected(newSelected)
    }, [entities])

    const deselectAll = useCallback(() => {
      setSelected({})
    }, [])

    const selectedList = useMemo(
      () =>
        !entities
          ? []
          : entities.filter(entity => selected[idSelector(entity)]),
      [entities, selected]
    )

    const selectedIds = useMemo(() => Object.keys(selected), [selected])

    const isAllSelected = useMemo(
      () => (!entities ? false : entities.length === selectedList.length),
      [entities, selectedList]
    )

    const value = useMemo(
      () => ({
        bulkSelectedList: selectedList,
        bulkAllSelected: isAllSelected,
        bulkSelectAll: selectAll,
        bulkDeselectAll: deselectAll,
        bulkToggle: toggleItem,
        bulkSelected: selected,
        bulkSelectedIds: selectedIds
      }),
      [
        selectedList,
        isAllSelected,
        selectAll,
        deselectAll,
        toggleItem,
        selected,
        selectedIds
      ]
    )

    return (
      <BulkActionContext.Provider value={value}>
        {children}
      </BulkActionContext.Provider>
    )
  }
)

BulkActionProvider.propTypes = {
  /** Array of entities, each entity should have a unique id property */
  entities: PropTypes.array.isRequired
}

export default BulkActionProvider
